import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import Globals from "../../utils/globals";

const IncidentTypesEditor = () => {
    const agencyId = useSelector(
        (state) => state.agencyStubs.selectedAgencyId
    );

    const agency = useSelector(
        (state) => state.agency
    );

    const incidentReportSchema =
        agency?.details?.report_schemas?.find(
            (schema) =>
                schema?.type === "public" &&
                schema?.subtype === "incident"
        );

    const reportSchemaId = incidentReportSchema?._id || null;
    const { getAccessTokenSilently } = useAuth0();

    // state variables declarations //
    const [incidentTypes, setIncidentTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [selectedLanguage, setSelectedLanguage] = useState("en_US");

    const [editingIndex, setEditingIndex] = useState(null);
    const [removingIncidentTypeIndex, setRemovingIncidentTypeIndex] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [draftTitle, setDraftTitle] = useState({});
    const [addingIncidentType, setAddingIncidentType] = useState(false);
    const [newIncidentTitle, setNewIncidentTitle] = useState({});

    // ------------------------------------------------------------
    // INCIDENT TYPES - ORIGINAL LOADED COPY
    // Keeps a snapshot of the incident types exactly as they were
    // loaded from Mongo so unsaved changes can be discarded safely.
    // ------------------------------------------------------------
    const [originalIncidentTypes, setOriginalIncidentTypes] = useState([]);
    // ------------------------------------------------------------
    // INCIDENT TYPES - SAVE STATUS
    // Prevents duplicate Save clicks while the PUT request is running.
    // ------------------------------------------------------------
    const [saving, setSaving] = useState(false);


    const load = async () => {
        if (!agencyId || !reportSchemaId) return;

        setLoading(true);
        setLoadError("");

        try {
            const token = await getAccessTokenSilently();

            const url =
                `${Globals.apiEndpoint}/dashboard/agencies/${agencyId}` +
                `/report_schemas/${reportSchemaId}/incident_types`;

            const resp = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const json = await resp.json().catch(() => ({}));

            if (!resp.ok) {
                setLoadError(
                    json?.message || "Failed to load incident types"
                );
                return;
            }

            // ------------------------------------------------------------
            // INCIDENT TYPES - STORE LOADED + ORIGINAL COPIES
            // incidentTypes is the editable working copy.
            // originalIncidentTypes remains untouched for Discard Changes.
            // ------------------------------------------------------------
            const loadedIncidentTypes = Array.isArray(json?.incident_types)
                ? json.incident_types
                : [];

            setIncidentTypes(loadedIncidentTypes);
            setOriginalIncidentTypes(
                JSON.parse(JSON.stringify(loadedIncidentTypes))
            );

            setHasUnsavedChanges(false);
        } catch (err) {
            console.error("[IncidentTypesEditor] load error:", err);
            setLoadError("Failed to load incident types");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [agencyId, reportSchemaId]);


    const discoveredLanguages = Array.from(
        new Set(
            incidentTypes.flatMap((item) =>
                Object.keys(item?.title || {})
            )
        )
    );

    const availableLanguages = [
        "en_US",
        ...discoveredLanguages.filter(
            (lang) => lang !== "en_US"
        ),
    ];

    const languageLabel = (lang) => {
        const labels = {
            en_US: "English",
            es_US: "Spanish",
        };

        return labels[lang] || lang;
    };


    const startIncidentTypeEdit = (item, index) => {
        setEditingIndex(index);
        setDraftTitle({ ...(item?.title || {}) });
    };

    const saveIncidentTypeEdit = () => {
        if (editingIndex === null) return;

        setIncidentTypes((current) =>
            current.map((item, index) =>
                index === editingIndex
                    ? {
                        ...item,
                        title: {
                            ...(item?.title || {}),
                            ...draftTitle,
                        },
                    }
                    : item
            )
        );

        setEditingIndex(null);
        setDraftTitle({});
        setHasUnsavedChanges(true);
    };

    const cancelIncidentTypeEdit = () => {
        setEditingIndex(null);
        setDraftTitle({});
    };

    const moveIncidentType = (index, direction) => {
        setIncidentTypes((current) => {
            const newIndex = index + direction;

            if (
                newIndex < 0 ||
                newIndex >= current.length
            ) {
                return current;
            }

            const updated = [...current];

            const [movedItem] = updated.splice(index, 1);
            updated.splice(newIndex, 0, movedItem);

            return updated;
        });

        setHasUnsavedChanges(true);
    };

    const removeIncidentType = () => {
        if (removingIncidentTypeIndex === null) return;

        setIncidentTypes((current) =>
            current.filter(
                (_, index) => index !== removingIncidentTypeIndex
            )
        );

        setRemovingIncidentTypeIndex(null);
        setHasUnsavedChanges(true);
    };

    const startAddIncidentType = () => {
        setNewIncidentTitle({});
        setAddingIncidentType(true);
    };

    const cancelAddIncidentType = () => {
        setNewIncidentTitle({});
        setAddingIncidentType(false);
    };

    const addIncidentType = () => {
        const englishTitle = newIncidentTitle?.en_US?.trim();

        if (!englishTitle) {
            return;
        }

        const newItem = {
            title: {
                ...newIncidentTitle,
                en_US: englishTitle,
            },
            should_be_aggregated: false,
            items: [],
            type: "list_option",
        };

        setIncidentTypes((current) => [
            ...current,
            newItem,
        ]);

        setNewIncidentTitle({});
        setAddingIncidentType(false);
        setHasUnsavedChanges(true);
    };


    // ------------------------------------------------------------
    // INCIDENT TYPES - DISCARD UNSAVED CHANGES
    // Restores the editor to the last version loaded from Mongo.
    // Does NOT make any request to the backend.
    // ------------------------------------------------------------
    const discardIncidentTypeChanges = () => {
        setIncidentTypes(
            JSON.parse(JSON.stringify(originalIncidentTypes))
        );

        setEditingIndex(null);
        setDraftTitle({});
        setRemovingIncidentTypeIndex(null);

        setAddingIncidentType(false);
        setNewIncidentTitle({});

        setHasUnsavedChanges(false);
    };

    // ------------------------------------------------------------
    // HANDLER: INCIDENT TYPES - SAVE CHANGES TO MONGO
    // Sends the complete ordered incident-type list to the existing
    // backend PUT endpoint. On success, the returned list becomes
    // both the new working copy and the new Discard baseline.
    // ------------------------------------------------------------
    const saveIncidentTypeChanges = async () => {
        if (!agencyId || !reportSchemaId || !hasUnsavedChanges || saving) {
            return;
        }

        setSaving(true);

        try {
            const token = await getAccessTokenSilently();

            const url =
                `${Globals.apiEndpoint}/dashboard/agencies/${agencyId}` +
                `/report_schemas/${reportSchemaId}/incident_types`;

            const resp = await fetch(url, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    incident_types: incidentTypes,
                }),
            });

            const json = await resp.json().catch(() => ({}));

            if (!resp.ok) {
                alert(
                    json?.message ||
                    "Failed to save Incident Types. No changes were confirmed."
                );
                return;
            }

            const savedIncidentTypes = Array.isArray(json?.incident_types)
                ? json.incident_types
                : [];

            // Use Mongo's returned version as the new source of truth.
            setIncidentTypes(savedIncidentTypes);

            setOriginalIncidentTypes(
                JSON.parse(JSON.stringify(savedIncidentTypes))
            );

            setHasUnsavedChanges(false);

            setEditingIndex(null);
            setDraftTitle({});
            setRemovingIncidentTypeIndex(null);
            setAddingIncidentType(false);
            setNewIncidentTitle({});

            alert("Incident Types saved successfully.");
        } catch (err) {
            console.error("[IncidentTypesEditor] save error:", err);

            alert(
                "Failed to save Incident Types. Please review your changes and try again."
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: 12 }}>
            <h4>Incident Types Editor</h4>

            <div style={{ marginBottom: 16 }}>
                <label
                    htmlFor="incident-language"
                    style={{ fontWeight: 600, marginRight: 10 }}
                >
                    Language View:
                </label>

                <select
                    id="incident-language"
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                >
                    {availableLanguages.map((lang) => (
                        <option key={lang} value={lang}>
                            {languageLabel(lang)}
                        </option>
                    ))}
                </select>
            </div>

            {/* ------------------------------------------------------------ */}
            {/* ADD INCIDENT TYPE FORM                                       */}
            {/* Displays English plus the selected translation language.     */}
            {/* Changes remain local until the user clicks Save Changes.     */}
            {/* ------------------------------------------------------------ */}

            {/* ------------------------------------------------------------ */}
            {/* INCIDENT TYPES - EDITOR ACTIONS                             */}
            {/* Matches the Resolution Form top-level action controls.      */}
            {/* Save appears only when there are unsaved local changes.     */}
            {/* ------------------------------------------------------------ */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginTop: 16,
                    marginBottom: 16,
                }}
            >
                {/* OPEN ADD INCIDENT TYPE FORM */}
                <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={startAddIncidentType}
                    disabled={addingIncidentType}
                    style={{
                        whiteSpace: "nowrap",
                    }}
                >
                    + Add Incident Type
                </button>

                {/* ---------------------------------------------------------- */}
                {/* SAVE INCIDENT TYPES TO DATABASE                           */}
                {/* Only shown when the editor has unsaved changes.           */}
                {/* ---------------------------------------------------------- */}
                {hasUnsavedChanges && (
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={saveIncidentTypeChanges}
                        disabled={saving}
                    >
                        {saving ? "Saving…" : "Save Incident Types"}
                    </button>
                )}

                {/* ---------------------------------------------------------- */}
                {/* RELOAD INCIDENT TYPES FROM DATABASE                       */}
                {/* Reloads the last saved Mongo version into the editor.     */}
                {/* ---------------------------------------------------------- */}
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={load}
                    disabled={loading}
                >
                    {loading ? "Loading…" : "Reload"}
                </button>
            </div>
            {addingIncidentType && (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            selectedLanguage === "en_US"
                                ? "1fr auto"
                                : "1fr 1fr auto",
                        gap: 12,
                        alignItems: "center",
                        padding: "12px",
                        marginBottom: 16,
                        border: "1px solid #ddd",
                        borderRadius: 4,
                        background: "#fafafa",
                    }}
                >
                    {/* ------------------------------------------------------------ */}
                    {/* NEW INCIDENT TYPE - ENGLISH INPUT                           */}
                    {/* Matches the Resolution Form field-name input styling.        */}
                    {/* ------------------------------------------------------------ */}
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Enter new English incident type here..."
                        value={newIncidentTitle.en_US || ""}
                        onChange={(e) =>
                            setNewIncidentTitle((current) => ({
                                ...current,
                                en_US: e.target.value,
                            }))
                        }
                        style={{
                            border: "2px solid #0d6efd",
                            background: "#fffdf2",
                            boxShadow: "0 0 0 2px rgba(13,110,253,.12)",
                        }}
                    />

                    {/* ------------------------------------------------------------ */}
                    {/* NEW INCIDENT TYPE - TRANSLATION INPUT                       */}
                    {/* Uses the same visual style as the English field.            */}
                    {/* ------------------------------------------------------------ */}
                    {selectedLanguage !== "en_US" && (
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter translation here..."
                            value={newIncidentTitle[selectedLanguage] || ""}
                            onChange={(e) =>
                                setNewIncidentTitle((current) => ({
                                    ...current,
                                    [selectedLanguage]: e.target.value,
                                }))
                            }
                            style={{
                                border: "2px solid #0d6efd",
                                background: "#fffdf2",
                                boxShadow: "0 0 0 2px rgba(13,110,253,.12)",
                            }}
                        />
                    )}

                    <div
                        style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                        }}
                    >
                        {/* ------------------------------------------------------------ */}
                        {/* ADD INCIDENT TYPE FORM - ACCEPT NEW INCIDENT TYPE            */}
                        {/* Adds the typed incident type to local editor state only.     */}
                        {/* ------------------------------------------------------------ */}
                        <button
                            type="button"
                            className="cfg-icon-btn"
                            title="Add incident type"
                            onClick={addIncidentType}
                            disabled={!newIncidentTitle?.en_US?.trim()}
                        >
                            <span
                                className="material-icons cfg-icon"
                                style={{ color: "#198754" }}
                            >
                                check
                            </span>
                        </button>
                        {/* ------------------------------------------------------------ */}
                        {/* ADD INCIDENT TYPE FORM - CANCEL                              */}
                        {/* Closes the Add form without changing the incident type list. */}
                        {/* ------------------------------------------------------------ */}
                        <button
                            type="button"
                            className="cfg-icon-btn"
                            title="Cancel"
                            onClick={cancelAddIncidentType}
                        >
                            <span className="material-icons cfg-icon cfg-close-icon">
                                close
                            </span>
                        </button>
                    </div>
                </div>
            )}


            {loading && <p>Loading incident types...</p>}

            {loadError && (
                <p style={{ color: "red" }}>{loadError}</p>
            )}

            {!loading && !loadError && (
                <div>
                    {incidentTypes.map((item, index) => (
                        <div
                            key={item._id || 'new-$index}'}
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    editingIndex === index
                                        ? selectedLanguage === "en_US"
                                            ? "1fr auto"
                                            : "1fr 1fr auto"
                                        : selectedLanguage === "en_US"
                                            ? "1fr auto"
                                            : "1fr 1fr auto",
                                gap: 12,
                                alignItems: "center",
                                padding: "8px 0",
                                borderBottom: "1px solid #eee",
                            }}
                        >
                            {editingIndex === index ? (
                                <>
                                    {/* English is always the base incident type */}
                                    <input
                                        type="text"
                                        value={draftTitle.en_US || ""}
                                        onChange={(e) =>
                                            setDraftTitle((current) => ({
                                                ...current,
                                                en_US: e.target.value,
                                            }))
                                        }
                                    />

                                    {/* When another language is selected, edit it beside English */}
                                    {selectedLanguage !== "en_US" && (
                                        <input
                                            type="text"
                                            value={draftTitle[selectedLanguage] || ""}
                                            onChange={(e) =>
                                                setDraftTitle((current) => ({
                                                    ...current,
                                                    [selectedLanguage]: e.target.value,
                                                }))
                                            }
                                        />
                                    )}

                                    <div
                                        style={{
                                            display: "flex",
                                            gap: 8,
                                            alignItems: "center",
                                        }}
                                    >
                                        {/* ------------------------------------------------------------ */}
                                        {/* INCIDENT TYPE EDIT - SAVE / ACCEPT EDIT                     */}
                                        {/* Saves the edited incident type to local React state only.   */}
                                        {/* ------------------------------------------------------------ */}
                                        <button
                                            type="button"
                                            className="cfg-icon-btn"
                                            title="Save incident type edit"
                                            onClick={saveIncidentTypeEdit}
                                        >
                                            <span
                                                className="material-icons cfg-icon"
                                                style={{ color: "#198754" }}
                                            >
                                                check
                                            </span>
                                        </button>

                                        {/* ------------------------------------------------------------ */}
                                        {/* INCIDENT TYPE EDIT - CANCEL EDIT                            */}
                                        {/* Discards the current edit and restores the displayed value. */}
                                        {/* ------------------------------------------------------------ */}
                                        <button
                                            type="button"
                                            className="cfg-icon-btn"
                                            title="Cancel incident type edit"
                                            onClick={cancelIncidentTypeEdit}
                                        >
                                            <span className="material-icons cfg-icon cfg-close-icon">
                                                close
                                            </span>
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* English label */}
                                    <div>
                                        {item?.title?.en_US || "(No English title)"}
                                    </div>

                                    {/* Translation beside English */}
                                    {selectedLanguage !== "en_US" && (
                                        <div>
                                            {/* ------------------------------------------------------------ */}
                                            {/* TRANSLATION DISPLAY                                           */}
                                            {/* Blank means no translation exists; the app falls back to      */}
                                            {/* English. Do not insert placeholder text into the title value. */}
                                            {/* ------------------------------------------------------------ */}
                                            {item?.title?.[selectedLanguage] || ""}
                                        </div>
                                    )}

                                    {/* Edit button */}
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        {/* MOVE INCIDENT TYPE UP */}
                                        <button
                                            type="button"
                                            className="cfg-icon-btn"
                                            title="Move incident type up"
                                            onClick={() => moveIncidentType(index, -1)}
                                            disabled={index === 0}
                                        >
                                            <span className="material-icons cfg-icon cfg-move-icon">
                                                keyboard_arrow_up
                                            </span>
                                        </button>

                                        {/* MOVE INCIDENT TYPE DOWN */}
                                        <button
                                            type="button"
                                            className="cfg-icon-btn"
                                            title="Move incident type down"
                                            onClick={() => moveIncidentType(index, 1)}
                                            disabled={index === incidentTypes.length - 1}
                                        >
                                            <span className="material-icons cfg-icon cfg-move-icon">
                                                keyboard_arrow_down
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            className="cfg-icon-btn"
                                            title="Edit incident type"
                                            onClick={() =>
                                                startIncidentTypeEdit(item, index)
                                            }
                                        >
                                            <span className="material-icons cfg-icon cfg-edit-icon">
                                                edit
                                            </span>
                                        </button>
                                        {/* REMOVE INCIDENT TYPE */}
                                        <button
                                            type="button"
                                            className="cfg-icon-btn"
                                            title="Remove incident type"
                                            onClick={() => setRemovingIncidentTypeIndex(index)}
                                        >
                                            <span className="material-icons cfg-icon cfg-remove-icon">
                                                block
                                            </span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {/* ------------------------------------------------------------ */}
            {/* REMOVE INCIDENT TYPE CONFIRMATION                           */}
            {/* Matches the Resolution Form remove-confirmation styling.    */}
            {/* Removal is local only until Save Incident Types is clicked. */}
            {/* ------------------------------------------------------------ */}

            {removingIncidentTypeIndex !== null && (
                <div
                    style={{
                        width: "100%",
                        marginTop: 10,
                        padding: "12px 14px",
                        background: "#fff8f8",
                        border: "1px solid #f1c2c7",
                        borderRadius: 8,
                    }}
                >
                    <div
                        style={{
                            marginBottom: 10,
                            fontWeight: 600,
                        }}
                    >
                        Remove "
                        {incidentTypes[removingIncidentTypeIndex]?.title?.en_US ||
                            "this incident type"}
                        " from this Incident Types list?
                    </div>

                    <div
                        style={{
                            display: "flex",
                            gap: 10,
                        }}
                    >
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={removeIncidentType}
                        >
                            Remove
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setRemovingIncidentTypeIndex(null)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IncidentTypesEditor;